import {
    ElementRef,
    Inject,
    Injectable,
    OnDestroy,
    Renderer2,
    RendererFactory2
} from '@angular/core';
import { UiStore, ViewportSize } from './ui.store';
import { ViewportRuler } from '@angular/cdk/overlay';
import { debounceTime, map, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { fromEvent, Observable, Subject } from 'rxjs';
import { SessionQuery } from '../../../core/state/session/session.query';
import { HttpClient } from '@angular/common/http';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { DOCUMENT } from '@angular/common';
import { Currency } from '../../components/currency-selector/currency-country-codes';
import { SbEventBusService } from 'src/app/legacy/services/sb-event-bus.service';
import { SbEventTopic } from 'src/app/legacy/services/sb-event-topic';

export const getBreakpoint = (width: number): ViewportSize => {
    if (width < 576) {
        return 'xs';
    } else if (width < 768) {
        return 'sm';
    } else if (width < 992) {
        return 'md';
    } else if (width < 1200) {
        return 'lg';
    } else if (width < 1400) {
        return 'xl';
    } else if (width < 1600) {
        return 'xxl';
    } else if (width < 2500) {
        return 'xxxl';
    } else {
        return 'xxxxl';
    }
};

@Injectable({ providedIn: 'root' })
export class UiService implements OnDestroy {
    unsubscribe$ = new Subject();
    private renderer: Renderer2;
    constructor(
        private http: HttpClient,
        private appConfig: AppConfigQuery,
        private uiStore: UiStore,
        private viewportRuler: ViewportRuler,
        private session: SessionQuery,
        private eventBus: SbEventBusService,
        rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private _document: HTMLDocument
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        viewportRuler
            .change(200)
            .pipe(
                map((_) => viewportRuler.getViewportSize().width),
                map((width) => getBreakpoint(width)),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((breakpoint) => this.setBreakpoint(breakpoint));
        // reset ui store when user id changes
        this.session.userSwitch$
            .pipe(
                tap((_) => this.uiStore.reset()),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // improve performance by disabling animations and transitions during window resize
        fromEvent(window, 'resize')
            .pipe(
                throttleTime(400),
                tap(() =>
                    this.renderer.addClass(
                        this._document.body,
                        'resize-animation-stopper'
                    )
                ),
                debounceTime(450),
                tap(() =>
                    this.renderer.removeClass(
                        this._document.body,
                        'resize-animation-stopper'
                    )
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    setUrl(url: string) {
        this.uiStore.update((state) => ({ ...state, url }));
    }
    setLandingPage(url: string) {
        this.uiStore.update((state) => ({ ...state, landingPage: url }));
    }

    setBreakpoint(breakpoint: ViewportSize) {
        this.uiStore.update((state) => ({ ...state, viewportSize: breakpoint }));
    }

    setScrollObservable(scrollTopObservable: Observable<number>) {
        this.uiStore.update((state) => ({ ...state, scrollTopObservable }));
    }

    setCurrentCurrency(currency: Currency) {
        this.uiStore.update((state) => ({ ...state, currentCurrency: currency }));

        // TODO: remove this once eventbus is refactored (e.g., have been able to get rid of all sb-grid components)
        this.eventBus.emit({
            source: 'base-currency-selection',
            topic: SbEventTopic.BaseCurrencySelected,
            payload: {
                value: currency
            }
        });
    }

    scrollToTop() {
        this._document.querySelector('.sb-layout-and-footer-container').scrollTo(0, 0);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
